import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface InvestmentTip {
  id: string;
  title: string;
  content: string;
  category: string;
  ticker_symbol?: string;
  company_name?: string;
  current_price?: number;
  target_price?: number;
  risk_level?: string;
  investment_horizon?: string;
  image_url?: string;
  video_url?: string;
  is_featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  rating: number;
  rating_count: number;
  view_count: number;
  meta_description?: string;
  keywords?: string[];
}

export const useInvestmentTips = () => {
  return useQuery({
    queryKey: ["investment-tips"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("investment_tips")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as InvestmentTip[];
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Cache for 15 minutes
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
};

export const useTodaysInvestmentTip = () => {
  return useQuery({
    queryKey: ["todays-investment-tip"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("investment_tips")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data as InvestmentTip | null;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Cache for 15 minutes
  });
};

export const useCreateInvestmentTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (investmentTip: Omit<InvestmentTip, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("investment_tips")
        .insert([investmentTip])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investment-tips"] });
      queryClient.invalidateQueries({ queryKey: ["todays-investment-tip"] });
      toast({
        title: "Success!",
        description: "Investment tip created successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create investment tip. Please try again.",
        variant: "destructive",
      });
      console.error("Error creating investment tip:", error);
    },
  });
};

export const useUpdateInvestmentTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<InvestmentTip> & { id: string }) => {
      const { data, error } = await supabase
        .from("investment_tips")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investment-tips"] });
      queryClient.invalidateQueries({ queryKey: ["todays-investment-tip"] });
      toast({
        title: "Success!",
        description: "Investment tip updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update investment tip. Please try again.",
        variant: "destructive",
      });
      console.error("Error updating investment tip:", error);
    },
  });
};

export const useDeleteInvestmentTip = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("investment_tips")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investment-tips"] });
      queryClient.invalidateQueries({ queryKey: ["todays-investment-tip"] });
      toast({
        title: "Success!",
        description: "Investment tip deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete investment tip. Please try again.",
        variant: "destructive",
      });
      console.error("Error deleting investment tip:", error);
    },
  });
};